import { useRef, useState } from 'react';
import ErrorMessages from '../../utils/errorMessage';
import { highlight, removeHighlight } from '../../utils/helpers';
import {
  EndpointTypes,
  ProtocolNetworkType,
  WorkspaceType,
} from './declaration';
import Footer from './footer';

interface Props {
  endpointData: EndpointTypes;
  step: number;
  workspaces: WorkspaceType[];
  setEndpointData: (obj: EndpointTypes) => void;
  nextStep: () => void;
  protocolNetworkTypeList: ProtocolNetworkType[];
}
interface networkType{
  network:string,
  networkName:string;
}

function Endpoint({
  endpointData,
  setEndpointData,
  nextStep,
  step,
  workspaces,
  protocolNetworkTypeList,
}: Props) {
  const [validationMsg, setValidationMsg] = useState<string>('');
  const [networkTypes, setNetworkTypes] = useState([] as networkType[]);
  const [allowNext, setAllowNext] = useState(true);
  const nameRef = useRef<HTMLInputElement>(null);
  const workspaceRef = useRef<HTMLSelectElement>(null);
  const networkRef = useRef<HTMLSelectElement>(null);
  const protocolRef = useRef<HTMLSelectElement>(null);

  const handleOnChange = (option: string, val: string) => {
    const namePattern = /^[A-Za-z]([A-Za-z0-9-]){3,48}[A-Za-z0-9]$/;
    let res;
    switch (option) {
      case 'name':
        res = val.match(namePattern);
        if (!(/[a-z]/i.test(val.charAt(0)))) {
          setValidationMsg(ErrorMessages.startWithAlphabetError('RPC Endpoint name'));
          setEndpointData({ ...endpointData, name: val });
          highlight(nameRef, 'border');
          setAllowNext(false);
          break;
        }
        if (val.length < 5) {
          setValidationMsg(ErrorMessages.minLengthError('RPC endpoint name', 5));
          setEndpointData({ ...endpointData, name: val });
          highlight(nameRef, 'border');
          setAllowNext(false);
          break;
        }
        if (val.length > 50) {
          setValidationMsg(ErrorMessages.maxLengthError('RPC endpoint name', 50));
          setEndpointData({ ...endpointData, name: val });
          highlight(nameRef, 'border');
          setAllowNext(false);
          break;
        }
        if ((!(/^[A-Za-z0-9]*$/.test(val.charAt(val.length - 1))))) {
          setValidationMsg(ErrorMessages.endWithAlphanumericError('RPC endpoint name'));
          setEndpointData({ ...endpointData, name: val });
          highlight(nameRef, 'border');
          setAllowNext(false);
          break;
        }
        if (!res) {
          setValidationMsg(ErrorMessages.endpointNameFormat());
          setEndpointData({ ...endpointData, name: val });
          highlight(nameRef, 'border');
          setAllowNext(false);
          break;
        } else {
          setEndpointData({ ...endpointData, name: val.trim() });
          setValidationMsg('');
          setAllowNext(true);
          removeHighlight(nameRef, 'border');
        }
        break;
      default:
        break;
    }
  };

  const next = () => {
    if (!allowNext) {
      return;
    }
    if (!endpointData || !endpointData.name) {
      highlight(nameRef, 'border');
      return;
    }
    if (!('workspaceId' in endpointData) || endpointData.workspaceId === '') {
      highlight(workspaceRef, 'border');
      return;
    }
    if (!('protocolName' in endpointData) || endpointData.protocolName === '') {
      highlight(protocolRef, 'border');
      return;
    }
    if (!('networkType' in endpointData) || endpointData.networkType === '') {
      highlight(networkRef, 'border');
    } else {
      nextStep();
    }
  };

  return (
    <>
      <div className="my-5 col-xl-10 col-lg-10 col-sm-11 col-md-11 col-11 mx-auto border-bottom pb-5">
        <div className="row mb-3 align-items-center">
          <div className="col-xl-6 col-lg-6 col-sm-6 col-md-6">
            <h6
              className="fw-bold"
            >
              Name Your RPC Endpoint
              <span className="text-danger">*</span>
            </h6>
            <input
              style={{ width: '75%' }}
              type="text"
              className="form-control"
              ref={nameRef}
              value={endpointData.name}
              onChange={(e) => handleOnChange('name', e.target.value)}
            />
          </div>
          <div className="col-xl-6 col-lg-6 col-sm-6 col-md-6">
            <div className="marginauto form-floating cordaversion">
              <h6 className="mx-3 fw-bold">
                Select Workspace
                <span className="text-danger">*</span>
              </h6>
              <select
                className="form-borderradius form-select mx-2 mt-3"
                style={{ height: '45px', width: '75%', lineHeight: '2' }}
                value={endpointData.workspaceId}
                id="z12"
                ref={workspaceRef}
                onChange={(e) => {
                  removeHighlight(workspaceRef, 'border');
                  setEndpointData({
                    ...endpointData,
                    workspaceId: e.target.value,
                  });
                }}
              >
                <option>Select WorkSpace</option>
                {workspaces.map((data, idx) => (
                  <option key={`work-${idx.toString()}`} value={data.id} selected={data.id === endpointData?.workspaceId}>
                    {data.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row mt-5">
          <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
            <div className="form-floating cordaversion">
              <h6 className=" fw-bold">
                Select Protocol
                <span className="text-danger">*</span>
              </h6>
              <select
                className="form-borderradius form-select mt-3"
                style={{ height: '45px', width: '75%', lineHeight: '2' }}
                value={protocolRef.current?.value}
                id="z13"
                ref={protocolRef}
                onChange={(e) => {
                  removeHighlight(protocolRef, 'border');
                  const selectedObj:ProtocolNetworkType[] = protocolNetworkTypeList.filter((item) => item.serviceId === e.target.value);
                  setEndpointData({
                    ...endpointData,
                    protocolName: selectedObj?.[0].serviceName,
                    serviceId: e.target.value,
                    networkType: '',
                  });
                  setNetworkTypes(selectedObj?.[0].type);
                }}
              >
                <option>Select Protocol</option>
                {protocolNetworkTypeList.map((data, idx) => (
                  <option key={`protocol-${idx.toString()}`} value={data.serviceId} selected={data.serviceId === endpointData?.serviceId}>
                    {data.serviceName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
            <div className="marginauto form-floating cordaversion">
              <h6 className="mx-3 fw-bold">
                Select Network Type
                <span className="text-danger">*</span>
              </h6>
              <select
                className="form-borderradius form-select mx-2 mt-3"
                style={{ height: '45px', width: '75%', lineHeight: '2' }}
                value={endpointData.networkType}
                id="z14"
                ref={networkRef}
                onChange={(e) => {
                  removeHighlight(networkRef, 'border');
                  setEndpointData({
                    ...endpointData,
                    networkType: e.target.value,
                    networkTypeName: networkTypes.filter((d) => d.network === e.target.value)?.[0].networkName,
                  });
                }}
              >
                <option>Select Network Type</option>
                {networkTypes && networkTypes.map((data, idx) => (
                  <option key={`protocol-${idx.toString()}`} value={data.network} selected={data.network === endpointData?.networkType}>
                    {data.networkName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {validationMsg && (
          <div className="text-center mt-5">
            <span style={{ color: 'red' }}>{validationMsg}</span>
          </div>
        )}
      </div>
      <Footer step={step} nextStep={next} disable={false} />
    </>
  );
}

export default Endpoint;
